import { atom } from "jotai";
import { authService } from "../services/auth";

/** Core auth state atoms — consumed by AuthContext hooks and AuthProvider */

// =============================================================================
// Core Auth State
// =============================================================================

export interface AuthState {
  token: string | null;
  refreshToken?: string | null;
  userId: string | null;
  isAdmin: boolean;
  adminUserId?: string | null;
  userType?: string | null;
  /**
   * Tri-state. `false` = external user has not signed their agreement and
   * PrivateRoute should bounce them to /agreement. `true` = signed, allow.
   * `null`/undefined = role has no agreement (admin/client) — allow.
   */
  agreementAccepted?: boolean | null;
  // isAuthenticated is accepted for backwards compat but ignored (it's derived)
  isAuthenticated?: boolean;
}

// Initialize from cookies/storage
const getInitialAuthState = (): AuthState => {
  // Always check stored auth first - real auth takes priority
  const stored = authService.getStoredAuth();
  return {
    token: stored.token,
    refreshToken: stored.refreshToken,
    userId: stored.userId,
    isAdmin: stored.isAdmin,
    adminUserId: stored.adminUserId,
    userType: stored.userType,
    agreementAccepted: stored.agreementAccepted,
  };
};

// Base auth state atom
export const authStateAtom = atom<AuthState>(getInitialAuthState());

// =============================================================================
// Derived Atoms (granular subscriptions)
// =============================================================================

// Individual field selectors - components subscribe only to what they need
export const tokenAtom = atom((get) => get(authStateAtom).token);
export const refreshTokenAtom = atom((get) => get(authStateAtom).refreshToken);
export const userIdAtom = atom((get) => get(authStateAtom).userId);
export const isAdminAtom = atom((get) => get(authStateAtom).isAdmin);
export const adminUserIdAtom = atom((get) => get(authStateAtom).adminUserId);
export const userTypeAtom = atom((get) => get(authStateAtom).userType);
export const agreementAcceptedAtom = atom((get) => get(authStateAtom).agreementAccepted);

// Computed: is the user authenticated?
export const isAuthenticatedAtom = atom((get) => {
  const state = get(authStateAtom);
  return Boolean(state.token && state.userId);
});

// =============================================================================
// Action Atoms (write-only)
// =============================================================================

// Update auth state (used after login, token refresh, etc.)
export const setAuthStateAtom = atom(null, (get, set, newState: AuthState) => {
  set(authStateAtom, newState);
});

// Partial update (for token refresh)
export const updateAuthStateAtom = atom(null, (get, set, update: Partial<AuthState>) => {
  set(authStateAtom, (prev) => ({ ...prev, ...update }));
});

// Clear auth state (logout)
export const clearAuthStateAtom = atom(null, (get, set) => {
  set(authStateAtom, {
    token: null,
    refreshToken: null,
    userId: null,
    isAdmin: false,
    adminUserId: null,
    userType: null,
    agreementAccepted: null,
  });
});

// =============================================================================
// Session Expiration State
// =============================================================================

// Controls visibility of session expired modal
export const sessionExpiredAtom = atom<boolean>(false);
